/**
 * Power Role - Format Pillar
 *
 * Formatação de valores de potência para exibição.
 *
 * @example
 * import { formatPower, getPowerSymbol, getPowerName } from '@attrx/role-morphic/power/format';
 *
 * formatPower('kilowatt', 2.5);                     // "2.5 kW"
 * formatPower('kilowatt', 2.5, { verbose: true });  // "2.5 kilowatts"
 * getPowerSymbol('kilowatt');                       // "kW"
 * getPowerName('kilowatt', 2);                      // "kilowatts"
 */

import { POWER_UNITS, type PowerUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface PowerFormatOptions {
  /** Número de casas decimais */
  decimals?: number;
  /** Usar nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação numérica */
  locale?: string;
  /** Notação: standard, scientific, compact */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de potência para exibição
 *
 * @param variant - Unidade
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatPower('kilowatt', 2.5);                     // "2.5 kW"
 * formatPower('kilowatt', 2.5, { verbose: true });  // "2.5 kilowatts"
 * formatPower('kilowatt', 1, { verbose: true });    // "1 kilowatt"
 * formatPower('watt', 1000, { decimals: 0 });       // "1000 W"
 */
export function formatPower(
  variant: PowerUnit,
  value: number,
  options: PowerFormatOptions = {},
): string {
  const config = POWER_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown power unit: ${variant}`);
  }

  const { decimals, verbose = false, locale, notation = "standard" } = options;

  // Format number
  let formattedNumber: string;

  if (locale || notation !== "standard") {
    const intlOptions: Intl.NumberFormatOptions = { notation };
    if (decimals !== undefined) {
      intlOptions.minimumFractionDigits = decimals;
      intlOptions.maximumFractionDigits = decimals;
    }
    formattedNumber = new Intl.NumberFormat(locale, intlOptions).format(value);
  } else if (decimals !== undefined) {
    formattedNumber = value.toFixed(decimals);
  } else {
    // Smart formatting: remove trailing zeros
    formattedNumber = Number(value.toPrecision(12)).toString();
  }

  // Get unit label
  const unitLabel = verbose
    ? value === 1
      ? config.singular
      : config.plural
    : config.symbol;

  return `${formattedNumber} ${unitLabel}`;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Retorna o símbolo de uma unidade
 *
 * @example
 * getPowerSymbol('kilowatt');              // "kW"
 * getPowerSymbol('horsepower_mechanical'); // "hp"
 */
export function getPowerSymbol(variant: PowerUnit): string {
  const config = POWER_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown power unit: ${variant}`);
  }
  return config.symbol;
}

/**
 * Retorna o nome de uma unidade (singular ou plural)
 *
 * @param variant - Unidade
 * @param count - Quantidade para determinar singular/plural (default: 1)
 *
 * @example
 * getPowerName('kilowatt', 1);  // "kilowatt"
 * getPowerName('kilowatt', 2);  // "kilowatts"
 */
export function getPowerName(variant: PowerUnit, count: number = 1): string {
  const config = POWER_UNITS[variant];
  if (!config) {
    throw new Error(`Unknown power unit: ${variant}`);
  }
  return count === 1 ? (config.singular || config.symbol) : (config.plural || config.symbol);
}
